// Tính chỉ số Dashboard Telesales từ dữ liệu thô (khách, cuộc gọi, lịch hẹn) + thao tác người dùng (overrides)
import dayjs from 'dayjs';
import type { Mang } from '../components/Types';
import {
  AGENTS, CLOSE_H, CONSULTANTS, DAY, HOUR, MANGS, BRANCHES, MIN, OPEN_H, SOURCES, rng,
  type Appt, type ApptStatus, type Call, type Lead, type Model, type Stage,
} from './data';

/** Ngưỡng vận hành (trưởng phòng cấu hình) */
export interface Cfg {
  /** Service level: cuộc gọi đến được nhận trong X giây */
  slaSec: number;
  /** Phải gọi lại cuộc nhỡ trong N phút */
  missedMin: number;
  /** Phải gọi khách mới trong N phút */
  newLeadMin: number;
  /** Khách S2–S4 không được chạm quá N ngày = bị bỏ quên (mặc định cho trạng thái không có ngưỡng riêng) */
  forgotDays: number;
  /** Ngưỡng riêng từng trạng thái (ngày); trạng thái không có trong đây thì không theo dõi bỏ quên — họp 30/10: cần nhiều lựa chọn hơn S2–S4 */
  forgotStages?: Record<string, number>;
  /** Thời gian hoàn tất sau cuộc gọi tối đa (phút) */
  acwMaxMin: number;
  /** Chỉ tiêu mỗi nhân viên / ngày */
  callTarget: number;
  bookTarget: number;
}
export const DEFAULT_CFG: Cfg = { slaSec: 20, missedMin: 30, newLeadMin: 15, forgotDays: 7, acwMaxMin: 5, callTarget: 45, bookTarget: 3, forgotStages: { S2: 7, S3: 7, S4: 7 } };
/** Trạng thái chọn được cho ngưỡng bỏ quên */
export const FORGOT_STAGES = ['S0', 'S1', 'S2', 'S3', 'S4'];
/** Số ngày không chạm thì coi là bỏ quên của một trạng thái (undefined = không theo dõi) */
export const forgotDaysOf = (cfg: Cfg, st: string): number | undefined =>
  cfg.forgotStages ? cfg.forgotStages[st] : ['S2', 'S3', 'S4'].includes(st) ? cfg.forgotDays : undefined;

export interface ApptPatch { status?: ApptStatus; consultantId?: string; amount?: number; reason?: string; by?: string; updatedAt?: number }
export interface Overrides {
  appt: Record<string, ApptPatch>;
  /** Việc đã xử lý: key việc → thời điểm */
  done: Record<string, number>;
  /** Việc giao lại: key việc → mã nhân viên */
  assign: Record<string, string>;
}
export interface Ctx { m: Model; now: number; cfg: Cfg; ov: Overrides; seedAt: number }
export interface Filter { mang?: Mang; branchId?: string; agentId?: string }

export type Period = 'today' | 'yesterday' | '7d' | 'month' | '30d';
export const PERIODS: { value: Period; label: string }[] = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'yesterday', label: 'Hôm qua' },
  { value: '7d', label: '7 ngày' },
  { value: 'month', label: 'Tháng này' },
  { value: '30d', label: '30 ngày' },
];
export interface Range { from: number; to: number }
export const rangeOf = (p: Period, now: number): Range => {
  const d = dayjs(now).startOf('day');
  if (p === 'yesterday') return { from: d.subtract(1, 'day').valueOf(), to: d.valueOf() - 1 };
  if (p === '7d') return { from: d.subtract(6, 'day').valueOf(), to: now };
  if (p === 'month') return { from: d.startOf('month').valueOf(), to: now };
  if (p === '30d') return { from: d.subtract(29, 'day').valueOf(), to: now };
  return { from: d.valueOf(), to: now };
};
const inR = (t: number, r: Range) => t >= r.from && t <= r.to;
export const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

/* ---------- lịch hẹn ---------- */
export const ARRIVED: ApptStatus[] = ['arrived', 'consulting', 'won', 'deposit', 'no_need', 'medical'];
export const CLOSED: ApptStatus[] = ['won', 'deposit'];
export const RESOLVED: ApptStatus[] = ['won', 'deposit', 'no_need', 'medical'];

/**
 * Trạng thái lịch: thao tác của tư vấn viên (overrides) ưu tiên; nếu chưa có thì mô phỏng theo thời điểm
 * mở dashboard đầu tiên trong ngày (seedAt) — lịch sau mốc đó chỉ đổi khi có người thao tác.
 */
export function apptStatus(a: Appt, c: Ctx): ApptStatus {
  const p = c.ov.appt[a.id];
  if (p?.status) return p.status;
  const s = c.seedAt;
  if (a.fate === 'cancelled' && a.bookedAt + DAY <= s) return 'cancelled';
  if (a.at + 90 * MIN <= s) return a.fate;
  const confirmed = a.confirmed && a.at - DAY <= s ? 'confirmed' : 'booked';
  if (a.at <= s) return a.fate === 'noshow' || a.fate === 'cancelled' ? confirmed : a.at + 30 * MIN <= s ? 'consulting' : 'arrived';
  return confirmed;
}
export const apptAmount = (a: Appt, c: Ctx) => {
  const st = apptStatus(a, c);
  return CLOSED.includes(st) ? c.ov.appt[a.id]?.amount ?? a.amount : 0;
};
export const apptConsultant = (a: Appt, c: Ctx) => {
  const p = c.ov.appt[a.id];
  if (p?.consultantId) return p.consultantId;
  return ['booked', 'confirmed', 'arrived', 'noshow', 'cancelled'].includes(apptStatus(a, c)) ? undefined : a.consultantId;
};
/** Lịch đã qua giờ hẹn mà khách chưa đến */
export const isLate = (a: Appt, c: Ctx, graceMin = 15) => ['booked', 'confirmed'].includes(apptStatus(a, c)) && a.at + graceMin * MIN < c.now;

/* ---------- khách ---------- */
const STAGE_BY_APPT: Record<ApptStatus, Stage> = {
  booked: 'S5', confirmed: 'S6', arrived: 'S6', consulting: 'S6', won: 'CS', deposit: 'CS',
  no_need: 'S6.1', medical: 'S6.2', noshow: 'S5.1', cancelled: 'S4',
};
export function leadStage(l: Lead, c: Ctx): Stage {
  if (l.apptId) {
    const a = c.m.apptById[l.apptId];
    if (a.bookedAt <= c.now) return STAGE_BY_APPT[apptStatus(a, c)];
  }
  return l.connectAt && l.connectAt <= c.now ? (l.stage0 === 'S5' ? 'S4' : l.stage0) : 'S0';
}
const visibleLead = (l: Lead, f: Filter) => (!f.mang || l.mang === f.mang) && (!f.branchId || l.branchId === f.branchId) && (!f.agentId || l.ownerId === f.agentId);

/* ---------- cuộc gọi ---------- */
/** Cuộc gọi trong khoảng (calls đã sắp theo thời gian) */
export function callsIn(m: Model, r: Range): Call[] {
  let lo = 0;
  let hi = m.calls.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (m.calls[mid].at < r.from) lo = mid + 1; else hi = mid; }
  const out: Call[] = [];
  for (let i = lo; i < m.calls.length && m.calls[i].at <= r.to; i++) out.push(m.calls[i]);
  return out;
}
const callMang = (cl: Call, m: Model) => (cl.leadId ? m.leadById[cl.leadId].mang : AGENTS.find(a => a.id === cl.agentId)?.mang ?? undefined);
const callVisible = (cl: Call, m: Model, f: Filter) => {
  if (f.agentId && cl.agentId !== f.agentId) return false;
  if (f.mang && callMang(cl, m) !== f.mang) return false;
  if (f.branchId && (!cl.leadId || m.leadById[cl.leadId].branchId !== f.branchId)) return false;
  return true;
};

export interface CallStats { inbound: number; answered: number; inSla: number; missed: number; asa: number; aht: number; out: number; connected: number; talkSec: number }
export function callStats(calls: Call[], cfg: Cfg): CallStats {
  let inbound = 0, answered = 0, inSla = 0, wait = 0, out = 0, connected = 0, handle = 0, handled = 0, talkSec = 0;
  for (const cl of calls) {
    if (cl.dir === 'in') {
      inbound++;
      if (cl.ok) { answered++; wait += cl.waitSec; if (cl.waitSec <= cfg.slaSec) inSla++; }
    } else {
      out++;
      if (cl.ok) connected++;
    }
    if (cl.ok) { handle += cl.talkSec + cl.acwSec; handled++; talkSec += cl.talkSec; }
  }
  return { inbound, answered, inSla, missed: inbound - answered, asa: answered ? Math.round(wait / answered) : 0, aht: handled ? Math.round(handle / handled) : 0, out, connected, talkSec };
}

/* ---------- trực tiếp: hàng chờ + trạng thái nhân viên ---------- */
export interface Waiting { id: string; phone: string; waitSec: number }
/** Cuộc đang chờ trong hàng (mô phỏng theo phút hiện tại; sẽ lấy từ tổng đài) */
export function liveQueue(now: number): Waiting[] {
  const d = dayjs(now);
  if (d.hour() < OPEN_H || d.hour() >= CLOSE_H) return [];
  const r = rng(`q-${d.format('YYYYMMDDHHmm')}`);
  const peak = (d.hour() >= 11 && d.hour() <= 13) || d.hour() >= 19;
  const n = Math.floor(r() * (peak ? 5 : 3));
  return Array.from({ length: n }, (_, i) => ({ id: `W${i}`, phone: `0${90 + Math.floor(r() * 9)}***${String(Math.floor(r() * 9999)).padStart(4, '0')}`, waitSec: 4 + Math.floor(r() * (peak ? 95 : 50)) }))
    .sort((a, b) => b.waitSec - a.waitSec);
}

export type AgentStatus = 'calling' | 'acw' | 'ready' | 'break' | 'offline';
export interface AgentLive { id: string; status: AgentStatus; since: number; reason?: string; leadId?: string; late: boolean }
export function agentLive(id: string, c: Ctx): AgentLive {
  const d = dayjs(c.now);
  const start = d.startOf('day').valueOf();
  if (d.hour() < OPEN_H || d.hour() >= CLOSE_H) return { id, status: 'offline', since: d.hour() < OPEN_H ? start : start + CLOSE_H * HOUR, late: false };
  // Nghỉ trưa xoay ca: mỗi nhân viên nghỉ 45 phút trong 11:30–13:45
  const idx = AGENTS.findIndex(a => a.id === id);
  const lunch = start + 11.5 * HOUR + idx * 25 * MIN;
  if (c.now >= lunch && c.now < lunch + 45 * MIN) return { id, status: 'break', reason: 'Nghỉ trưa', since: lunch, late: c.now - lunch > 60 * MIN };
  const mine = callsIn(c.m, { from: c.now - 2 * HOUR, to: c.now }).filter(cl => cl.agentId === id && cl.ok);
  const last = mine[mine.length - 1];
  if (last) {
    const talkEnd = last.at + last.talkSec * 1000;
    if (c.now < talkEnd) return { id, status: 'calling', since: last.at, leadId: last.leadId, late: false };
    if (c.now < talkEnd + last.acwSec * 1000) return { id, status: 'acw', since: talkEnd, leadId: last.leadId, late: c.now - talkEnd > c.cfg.acwMaxMin * MIN };
    return { id, status: 'ready', since: talkEnd + last.acwSec * 1000, late: false };
  }
  return { id, status: 'ready', since: Math.max(start + OPEN_H * HOUR, c.now - 2 * HOUR), late: false };
}

/** Tỷ lệ bận: thời gian nói + hoàn tất / thời gian trực (trừ nghỉ trưa) hôm nay */
export function occupancy(c: Ctx, f: Filter = {}) {
  const d = dayjs(c.now);
  const open = d.startOf('day').valueOf() + OPEN_H * HOUR;
  const end = Math.min(c.now, d.startOf('day').valueOf() + CLOSE_H * HOUR);
  if (end <= open) return 0;
  const agents = AGENTS.filter(a => (!f.agentId || a.id === f.agentId) && (!f.mang || !a.mang || a.mang === f.mang));
  const busy = callsIn(c.m, { from: open, to: end }).filter(cl => cl.ok && agents.some(a => a.id === cl.agentId)).reduce((s, cl) => s + cl.talkSec + cl.acwSec, 0) * 1000;
  const avail = agents.length * Math.max(1, end - open - Math.min(45 * MIN, Math.max(0, end - (open + 3.5 * HOUR))));
  return Math.min(100, pct(busy, avail));
}

/* ---------- việc cần xử lý ---------- */
export type TaskKind = 'missed' | 'new' | 'callback' | 'confirm' | 'noshow' | 'forgot';
export const TASK_META: Record<TaskKind, { label: string; short: string; color: string }> = {
  missed: { label: 'Gọi lại cuộc nhỡ', short: 'Cuộc nhỡ', color: '#ef4444' },
  new: { label: 'Gọi khách mới', short: 'Khách mới', color: '#3e91ab' },
  callback: { label: 'Hẹn gọi lại', short: 'Hẹn gọi lại', color: '#6366f1' },
  confirm: { label: 'Xác nhận lịch hẹn', short: 'Xác nhận lịch', color: '#0ea5e9' },
  noshow: { label: 'Khách không đến — đặt lại lịch', short: 'Không đến', color: '#f59e0b' },
  forgot: { label: 'Khách bị bỏ quên', short: 'Bỏ quên', color: '#94a3b8' },
};
export interface Task { key: string; kind: TaskKind; agentId: string; due: number; phone: string; leadId?: string; apptId?: string; at: number; detail: string }

/** Lần chạm gần nhất với từng khách tính tới `now` */
export function lastTouch(c: Ctx): Record<string, number> {
  const t: Record<string, number> = {};
  for (const cl of callsIn(c.m, { from: 0, to: c.now })) if (cl.leadId) t[cl.leadId] = cl.at;
  return t;
}

export function tasks(c: Ctx, f: Filter = {}): Task[] {
  const { m, now, cfg, ov } = c;
  const today = dayjs(now).startOf('day').valueOf();
  const out: Task[] = [];
  const push = (t: Task) => {
    if (ov.done[t.key]) return;
    const agentId = ov.assign[t.key] ?? t.agentId;
    if (f.agentId && agentId !== f.agentId) return;
    out.push({ ...t, agentId });
  };
  const recent = callsIn(m, { from: today - 3 * DAY, to: now });
  const touch = lastTouch(c);

  // Cuộc gọi đến bị nhỡ mà chưa có cuộc gọi đi nào tới số đó sau đó
  recent.forEach((cl, i) => {
    if (cl.dir !== 'in' || cl.ok || !callVisible(cl, m, { mang: f.mang, branchId: f.branchId })) return;
    if (recent.slice(i + 1).some(x => x.phone === cl.phone && (x.dir === 'out' || x.ok))) return;
    push({ key: `missed:${cl.id}`, kind: 'missed', agentId: cl.agentId, due: cl.at + cfg.missedMin * MIN, phone: cl.phone, leadId: cl.leadId, at: cl.at, detail: `Gọi đến lúc ${dayjs(cl.at).format('HH:mm DD/MM')} · chờ ${cl.waitSec}s rồi gác` });
  });

  for (const l of m.leads) {
    if (l.createdAt > now || l.createdAt < today - 30 * DAY || !visibleLead(l, { mang: f.mang, branchId: f.branchId })) continue;
    // Khách mới chưa được gọi lần nào
    if (l.createdAt >= today - 3 * DAY && l.source !== 'Hotline' && !touch[l.id]) {
      push({ key: `new:${l.id}`, kind: 'new', agentId: l.ownerId, due: l.createdAt + cfg.newLeadMin * MIN, phone: l.phone, leadId: l.id, at: l.createdAt, detail: `Nguồn ${l.source} · vào lúc ${dayjs(l.createdAt).format('HH:mm DD/MM')}` });
      continue;
    }
    const st = leadStage(l, c);
    // Hẹn gọi lại tới hạn hôm nay (hoặc đã trễ) mà chưa gọi
    if (l.callbackAt && !l.apptId && l.callbackAt >= today - 3 * DAY && l.callbackAt < today + DAY && !(touch[l.id] >= l.callbackAt - 10 * MIN)) {
      push({ key: `callback:${l.id}`, kind: 'callback', agentId: l.ownerId, due: l.callbackAt, phone: l.phone, leadId: l.id, at: l.callbackAt, detail: `Khách hẹn gọi lại ${dayjs(l.callbackAt).format('HH:mm DD/MM')} · ${st}` });
      continue;
    }
    // Khách đang theo dõi bị bỏ quên
    const fd = forgotDaysOf(cfg, st);
    if (fd != null && !l.apptId && touch[l.id] && now - touch[l.id] > fd * DAY) {
      push({ key: `forgot:${l.id}`, kind: 'forgot', agentId: l.ownerId, due: touch[l.id] + fd * DAY, phone: l.phone, leadId: l.id, at: touch[l.id], detail: `${st} · chạm lần cuối ${dayjs(touch[l.id]).format('DD/MM')} (${Math.floor((now - touch[l.id]) / DAY)} ngày)` });
    }
  }

  for (const a of m.appts) {
    const l = m.leadById[a.leadId];
    if (a.bookedAt > now || !visibleLead(l, { mang: f.mang, branchId: f.branchId })) continue;
    const st = apptStatus(a, c);
    // Lịch chưa xác nhận: hôm nay (hạn trước giờ hẹn 2 tiếng) và ngày mai (hạn 18:00 hôm nay)
    if (st === 'booked' && a.at > now && a.at < today + 2 * DAY) {
      const tomorrow = a.at >= today + DAY;
      push({ key: `confirm:${a.id}`, kind: 'confirm', agentId: a.bookedBy, due: tomorrow ? today + 18 * HOUR : Math.max(a.bookedAt, a.at - 2 * HOUR), phone: l.phone, leadId: l.id, apptId: a.id, at: a.at, detail: `Lịch ${a.kind.toLowerCase()} ${dayjs(a.at).format('HH:mm')} ${tomorrow ? 'ngày mai' : 'hôm nay'}` });
    }
    if (st === 'noshow' && a.at >= today - 3 * DAY && a.at <= now) {
      push({ key: `noshow:${a.id}`, kind: 'noshow', agentId: a.bookedBy, due: a.at + DAY, phone: l.phone, leadId: l.id, apptId: a.id, at: a.at, detail: `Không đến lịch ${dayjs(a.at).format('HH:mm DD/MM')}` });
    }
  }
  // Việc trễ lên trước, trong đó việc gấp (cuộc nhỡ, khách mới) ưu tiên hơn; còn lại theo hạn
  const rank = (t: Task) => (t.due < now ? TASK_PRIORITY.indexOf(t.kind) : TASK_PRIORITY.length);
  return out.sort((a, b) => rank(a) - rank(b) || a.due - b.due);
}
const TASK_PRIORITY: TaskKind[] = ['missed', 'new', 'callback', 'noshow', 'confirm', 'forgot'];
export const isOverdue = (t: Task, now: number) => t.due < now;

/* ---------- phễu ---------- */
export const FUNNEL = ['Khách mới', 'Đã gọi', 'Kết nối', 'Đặt lịch', 'Đến cơ sở', 'Chốt / Cọc'] as const;
/** Phễu theo nhóm khách vào trong kỳ (cohort) */
export function funnel(c: Ctx, r: Range, f: Filter = {}): number[] {
  const touch = lastTouch(c);
  const v = [0, 0, 0, 0, 0, 0];
  for (const l of c.m.leads) {
    if (!inR(l.createdAt, r) || l.createdAt > c.now || !visibleLead(l, f)) continue;
    v[0]++;
    if (!touch[l.id]) continue;
    v[1]++;
    if (!l.connectAt || l.connectAt > c.now) continue;
    v[2]++;
    const a = l.apptId ? c.m.apptById[l.apptId] : undefined;
    if (!a || a.bookedAt > c.now) continue;
    v[3]++;
    const st = apptStatus(a, c);
    if (ARRIVED.includes(st)) v[4]++;
    if (CLOSED.includes(st)) v[5]++;
  }
  return v;
}

/* ---------- hiệu suất ---------- */
export interface Perf {
  id: string;
  out: number; connected: number; inbound: number; answered: number; aht: number;
  booked: number; due: number; arrived: number; closed: number; revenue: number; firstCallMin: number | null;
}
const emptyPerf = (id: string): Perf => ({ id, out: 0, connected: 0, inbound: 0, answered: 0, aht: 0, booked: 0, due: 0, arrived: 0, closed: 0, revenue: 0, firstCallMin: null });

/** Hiệu suất theo nhân viên Telesales: cuộc gọi trong kỳ + lịch do NV đặt trong kỳ */
export function agentPerf(c: Ctx, r: Range, f: Filter = {}): Perf[] {
  const calls = callsIn(c.m, { from: r.from, to: Math.min(r.to, c.now) }).filter(cl => callVisible(cl, c.m, { mang: f.mang, branchId: f.branchId }));
  return AGENTS.filter(a => !f.agentId || a.id === f.agentId).map(a => {
    const mine = calls.filter(cl => cl.agentId === a.id);
    const s = callStats(mine, c.cfg);
    const p: Perf = { ...emptyPerf(a.id), out: s.out, connected: s.connected, inbound: s.inbound, answered: s.answered, aht: s.aht };
    for (const ap of c.m.appts) {
      if (ap.bookedBy !== a.id || !inR(ap.bookedAt, r) || ap.bookedAt > c.now || !visibleLead(c.m.leadById[ap.leadId], { mang: f.mang, branchId: f.branchId })) continue;
      p.booked++;
      const st = apptStatus(ap, c);
      if (ap.at <= c.now && st !== 'cancelled') p.due++;
      if (ARRIVED.includes(st)) p.arrived++;
      if (CLOSED.includes(st)) { p.closed++; p.revenue += apptAmount(ap, c); }
    }
    p.firstCallMin = firstCallMin(c, r, { ...f, agentId: a.id });
    return p;
  });
}

/** Tốc độ gọi khách mới: trung vị số phút từ lúc khách vào đến cuộc gọi đầu tiên (khách vào trong kỳ) */
export function firstCallMin(c: Ctx, r: Range, f: Filter = {}): number | null {
  const first: Record<string, number> = {};
  for (const cl of callsIn(c.m, { from: r.from, to: c.now })) if (cl.leadId && cl.dir === 'out' && !(cl.leadId in first)) first[cl.leadId] = cl.at;
  const v = c.m.leads
    .filter(l => inR(l.createdAt, r) && l.source !== 'Hotline' && visibleLead(l, f) && first[l.id])
    .map(l => (first[l.id] - l.createdAt) / MIN)
    .sort((a, b) => a - b);
  return v.length ? Math.round(v[Math.floor(v.length / 2)]) : null;
}

export type BreakBy = 'mang' | 'source' | 'branch';
export interface BreakRow { key: string; label: string; leads: number; connected: number; booked: number; arrived: number; closed: number; revenue: number }
/** Bảng cắt theo mảng / nguồn / chi nhánh — theo khách vào trong kỳ */
export function breakdown(c: Ctx, r: Range, by: BreakBy, f: Filter = {}): BreakRow[] {
  const keys: [string, string][] = by === 'mang' ? MANGS.map(x => [x, x]) : by === 'source' ? SOURCES.map(x => [x, x]) : BRANCHES.map(b => [b.id, b.name]);
  const rows = new Map<string, BreakRow>(keys.map(([key, label]) => [key, { key, label, leads: 0, connected: 0, booked: 0, arrived: 0, closed: 0, revenue: 0 }]));
  for (const l of c.m.leads) {
    if (!inR(l.createdAt, r) || l.createdAt > c.now || !visibleLead(l, f)) continue;
    const row = rows.get(by === 'mang' ? l.mang : by === 'source' ? l.source : l.branchId);
    if (!row) continue;
    row.leads++;
    if (l.connectAt && l.connectAt <= c.now) row.connected++;
    const a = l.apptId ? c.m.apptById[l.apptId] : undefined;
    if (!a || a.bookedAt > c.now) continue;
    row.booked++;
    const st = apptStatus(a, c);
    if (ARRIVED.includes(st)) row.arrived++;
    if (CLOSED.includes(st)) { row.closed++; row.revenue += apptAmount(a, c); }
  }
  return [...rows.values()];
}

/* ---------- bản đồ nhiệt ---------- */
export type HeatKind = 'in' | 'missed' | 'connect';
export const HEAT_HOURS = Array.from({ length: CLOSE_H - OPEN_H }, (_, i) => i + OPEN_H);
export const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
/** Trung bình theo thứ × giờ trong 4 tuần gần nhất (không tính hôm nay). Giá trị: số cuộc / ngày, hoặc % kết nối */
export function heat(c: Ctx, kind: HeatKind, f: Filter = {}): number[][] {
  const today = dayjs(c.now).startOf('day').valueOf();
  const num = WEEKDAYS.map(() => HEAT_HOURS.map(() => 0));
  const den = WEEKDAYS.map(() => HEAT_HOURS.map(() => 0));
  for (const cl of callsIn(c.m, { from: today - 28 * DAY, to: today - 1 })) {
    if (!callVisible(cl, c.m, f)) continue;
    const d = dayjs(cl.at);
    const wi = (d.day() + 6) % 7;
    const hi = d.hour() - OPEN_H;
    if (hi < 0 || hi >= HEAT_HOURS.length) continue;
    if (kind === 'connect') { if (cl.dir === 'out') { den[wi][hi]++; if (cl.ok) num[wi][hi]++; } }
    else if (cl.dir === 'in' && (kind === 'in' || !cl.ok)) num[wi][hi]++;
  }
  return num.map((row, wi) => row.map((v, hi) => (kind === 'connect' ? pct(v, den[wi][hi]) : Math.round((v / 4) * 10) / 10)));
}

/* ---------- cơ sở ---------- */
export interface BranchKpi { total: number; arrived: number; waiting: number; consulting: number; closed: number; resolved: number; revenue: number; deposit: number; noshow: number; late: number }
export function branchKpi(list: Appt[], c: Ctx): BranchKpi {
  const k: BranchKpi = { total: 0, arrived: 0, waiting: 0, consulting: 0, closed: 0, resolved: 0, revenue: 0, deposit: 0, noshow: 0, late: 0 };
  for (const a of list) {
    const st = apptStatus(a, c);
    if (st === 'cancelled') continue;
    k.total++;
    if (ARRIVED.includes(st)) k.arrived++;
    if (st === 'arrived') k.waiting++;
    if (st === 'consulting') k.consulting++;
    if (RESOLVED.includes(st)) k.resolved++;
    if (CLOSED.includes(st)) k.closed++;
    if (st === 'won') k.revenue += apptAmount(a, c);
    if (st === 'deposit') k.deposit += apptAmount(a, c);
    if (st === 'noshow') k.noshow++;
    if (isLate(a, c)) k.late++;
  }
  return k;
}
export const apptsOn = (c: Ctx, day: number, branchId?: string) =>
  c.m.appts.filter(a => a.at >= day && a.at < day + DAY && a.bookedAt <= c.now && (!branchId || a.branchId === branchId));

export interface ConsultPerf { id: string; served: number; closed: number; revenue: number; deposit: number }
export function consultantPerf(c: Ctx, r: Range, branchId?: string): ConsultPerf[] {
  const day = { from: r.from, to: dayjs(r.to).endOf('day').valueOf() };
  return CONSULTANTS.filter(x => !branchId || x.branchId === branchId).map(x => {
    const p: ConsultPerf = { id: x.id, served: 0, closed: 0, revenue: 0, deposit: 0 };
    for (const a of c.m.appts) {
      // Tính theo ngày hẹn (khách có thể đến sớm hơn giờ hẹn)
      if (!inR(a.at, day) || apptConsultant(a, c) !== x.id) continue;
      const st = apptStatus(a, c);
      if (!RESOLVED.includes(st)) continue;
      p.served++;
      if (CLOSED.includes(st)) p.closed++;
      if (st === 'won') p.revenue += apptAmount(a, c);
      if (st === 'deposit') p.deposit += apptAmount(a, c);
    }
    return p;
  });
}

/** Khách cơ sở trả về Telesales chăm tiếp: không đến hoặc đến nhưng chưa có nhu cầu (7 ngày gần nhất) */
export const returnedToTele = (c: Ctx, branchId?: string) =>
  c.m.appts
    .filter(a => a.at >= c.now - 7 * DAY && a.at <= c.now && (!branchId || a.branchId === branchId) && ['noshow', 'no_need'].includes(apptStatus(a, c)))
    .sort((a, b) => b.at - a.at);

/* ---------- góc nhìn nhân viên ---------- */
export const STAGES: Stage[] = ['S0', 'S1', 'S2', 'S3', 'S4', 'S5', 'S5.1', 'S6', 'S6.1', 'S6.2', 'CS'];
export const STAGE_LABEL: Record<Stage, string> = {
  S0: 'Chưa tiếp cận', S1: 'Tò mò', S2: 'Tìm hiểu sơ bộ', S3: 'Có nhu cầu, còn rào cản', S4: 'Nhu cầu cao, cân nhắc',
  S5: 'Đã đặt lịch', 'S5.1': 'Đặt lịch chưa tới', S6: 'Xác nhận lịch', 'S6.1': 'Đến, chưa có nhu cầu', 'S6.2': 'Rớt do bệnh lý', CS: 'Đã chốt — chuyển CSKH',
};
/** Danh sách khách của nhân viên theo mã S (khách vào trong 30 ngày) */
export function pipeline(c: Ctx, agentId: string): Record<Stage, number> {
  const out = Object.fromEntries(STAGES.map(s => [s, 0])) as Record<Stage, number>;
  for (const l of c.m.leads) if (l.ownerId === agentId && l.createdAt <= c.now && l.createdAt >= c.now - 30 * DAY) out[leadStage(l, c)]++;
  return out;
}
